import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
  withGoogleMapsState,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import MapPinMarker from '@atom/components/common/map/markers/MapPinMarker';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import { getLoggedInUserMapLocationSelector } from '@atom/selectors/mapSelectors';
import {
  LocationUpdatePayload,
  MapEditMetadata,
  MapEditMetadataType,
  MapParams,
} from '@atom/types/map';
import { PolicyAction } from '@atom/types/policy';
import { InventoryAssetDetailState } from '@atom/types/store';
import { hasAccess } from '@atom/utilities/accessUtilities';
import {
  getGeoJsonFromMapBounds,
  getGoogleShapeCenter,
  getMapCenterAndZoomParams,
  getMapEditMetadata,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

const INVENTORY_DETAIL_ZOOM = 14;

interface Props {
  onSave: (payload: LocationUpdatePayload) => void;
  asset: InventoryAssetDetailState;
}

const InventoryDetailMap = ({ asset, onSave }: Props) => {
  const currentUserLocation = useSelector(getLoggedInUserMapLocationSelector);
  const [{ center, zoom, searchPoint }, { updateSearchParams }] = useNavigation(
    {
      center: SearchParamType.STRING,
      zoom: SearchParamType.NUMBER,
      searchPoint: SearchParamType.STRING,
    },
  );

  const dispatch = useGoogleMapsStateUpdater();
  const { isLoaded } = useGoogleMapsStateValue();
  const preferences = usePreferences();

  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState<string>(null);

  useEffect(() => {
    dispatch({
      type: 'SET',
      payload: {
        key: 'grab',
        data: false,
      },
    });
  }, []);

  useEffect(() => {
    const shapeCenter = getGoogleShapeCenter(asset?.location);
    updateSearchParams({
      zoom: INVENTORY_DETAIL_ZOOM,
      center: `${shapeCenter?.lat},${shapeCenter?.lng}`,
    });
  }, []);

  const onLoad = useCallback(setMap, []);

  const onIdle = useCallback(() => {
    try {
      const existingMapParams: MapParams = {
        center,
        zoom,
        searchPoint,
      };

      const mapParams = getMapCenterAndZoomParams(
        map,
        existingMapParams,
        preferences,
        currentUserLocation,
      );

      if (!R.equals(existingMapParams, mapParams) || !coordinates) {
        updateSearchParams({ zoom: mapParams.zoom, center: mapParams.center });
        R.pipe(
          getGeoJsonFromMapBounds,
          R.prop('coordinates'),
          JSON.stringify,
          setCoordinates,
        )(map);
      }
    } catch {
      updateSearchParams({ zoom: null, center: null });
    }
  }, [
    map,
    center,
    zoom,
    searchPoint,
    coordinates,
    preferences,
    updateSearchParams,
  ]);

  const canUpdateLocation = hasAccess(
    PolicyAction.UPDATE_LOCATION,
    asset.actions,
  );

  const mapEditMetadata: MapEditMetadata[] = useMemo(
    () => [
      getMapEditMetadata(asset, canUpdateLocation, MapEditMetadataType.ASSET),
    ],
    [asset],
  );

  const mapData = { assets: [asset] };

  return !isLoaded ? (
    <Progress style={{ height: '100%' }} />
  ) : (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      mapStyleControl
      layerControls
      mapSearchBox
      editControls
      mapEditMetadata={mapEditMetadata}
      onSave={onSave}
      locationEditZoom={INVENTORY_DETAIL_ZOOM}
    >
      <MapMarkers onClick={() => {}} data={mapData} />
      {searchPoint && <MapPinMarker center={searchPoint} />}
    </Map>
  );
};

export default withGoogleMapsState<Props>(InventoryDetailMap);
